import {
  getOnboardingBooks,
  getOnboardingBooksSubtitle,
  type OnboardingSubject,
} from '@/models/onboarding.personalization'
import type { EntryBook18361 } from '@/models/interfaces'
import { backend } from '@/services/backend'
import { storage } from '@/helpers/storage'
import { StorageKeys } from '@/helpers/storage.keys'
import { assertNotNull } from '@/helpers/typing'

const ONBOARDING_BOOK_TAGS_BY_SUBJECT = {
  'Getting ahead professionally': [
    'Business',
    'Economics',
    'Entrepreneurship',
    'Marketing',
    'Sales',
    'Management/Leadership',
  ],
  'Taking control of my finances': ['Money'],
  'Becoming a better partner or parent': [
    'Relationships',
    'Parenting',
    'Communication',
  ],
  'Being more focused and productive': [
    'Motivation',
    'Productivity',
    'Self-Improvement',
  ],
  'Improving my health and wellness': [
    'Spirituality',
    'Lifestyle',
    'Food',
    'Health',
  ],
} as const satisfies Partial<Record<OnboardingSubject, string[]>>

const ONBOARDING_SUBJECT_BY_BOOK_TAGS = Object.fromEntries(
  Object.entries(ONBOARDING_BOOK_TAGS_BY_SUBJECT).flatMap(([subject, tags]) =>
    tags.map((tag) => [tag, subject]),
  ),
) as Record<string, OnboardingSubject>

/**
 * Onboarding subject for the stored entry book category.
 */
export function getEntryBookSubject18361(): OnboardingSubject {
  const entryBook = getEntryBook18361()
  return ONBOARDING_SUBJECT_BY_BOOK_TAGS[entryBook.tags[0]]
}

/**
 * Entry book first, then peer slugs from the given subject.
 */
export function getEntryBookSlugs18361(
  subject: OnboardingSubject,
  peerCount = 4,
): string[] {
  const entryBook = getEntryBook18361()

  const peers = getOnboardingBooks(subject).filter(
    (book) => book !== entryBook.url_slug,
  )

  return [entryBook.url_slug, ...peers.slice(0, peerCount)]
}

/**
 * Subtitle phrase from the entry book category tag.
 */
export function getEntryBooksSubtitle18361(): string {
  const subject = getEntryBookSubject18361()

  return getOnboardingBooksSubtitle(subject)
}

/**
 * After signup, resolve the entry book from the user's saved URLs and store it
 * for onboarding personalization.
 */
export async function detectAndSaveEntryBook18361(): Promise<EntryBook18361 | null> {
  try {
    const entryBook = await backend.getExperimentEntryBook18361()
    storage.setItem(StorageKeys.entryBook18361, JSON.stringify(entryBook))
    return entryBook
  } catch {
    return null
  }
}

export function getStoredEntryBook18361(): EntryBook18361 {
  return getEntryBook18361()
}

export function getEntryBook18361(): EntryBook18361 {
  const data = assertNotNull(
    storage.getItem(StorageKeys.entryBook18361),
    'Entry book not found in storage',
  )

  try {
    return JSON.parse(data) as EntryBook18361
  } catch {
    throw new Error('Failed to parse entry book from storage')
  }
}
